import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API, timeout: 60000 });

// Attach admin Bearer token automatically to admin routes
const ADMIN_TOKEN_KEY = "hac_admin_token";
export const getAdminToken = () => localStorage.getItem(ADMIN_TOKEN_KEY);
export const setAdminToken = (t) => {
  if (t) localStorage.setItem(ADMIN_TOKEN_KEY, t);
  else localStorage.removeItem(ADMIN_TOKEN_KEY);
};
client.interceptors.request.use((cfg) => {
  const url = cfg.url || "";
  // Bidder-scoped endpoints prefer the bidder token; admin-scoped use the admin token.
  const isBidderScope = url.startsWith("/bidder/") || (url.startsWith("/auction/") && url.endsWith("/bid"));
  const isPublicBidder = url === "/bidder/register" || url === "/bidder/login";
  if (isBidderScope && !isPublicBidder) {
    const bt = localStorage.getItem("hac_bidder_token");
    if (bt) { cfg.headers.Authorization = `Bearer ${bt}`; return cfg; }
  }
  // Default: admin token (unconditional, backend ignores if endpoint is public)
  const t = getAdminToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// Response interceptor — capture critical CORS/network failures on admin
// requests and broadcast them as a window event so the AdminHealthBanner
// component (only mounted inside /admin) can surface them to ops with a
// pre-filled Emergent Support link. Public visitors never see this banner.
client.interceptors.response.use(
  (r) => r,
  (err) => {
    try {
      const url = err?.config?.url || "";
      const isAdmin = url.startsWith("/admin/") || url === "/admin";
      if (isAdmin && (!err.response || err.response.status >= 500)) {
        const detail = {
          ts: new Date().toISOString(),
          url: (err?.config?.baseURL || "") + url,
          method: (err?.config?.method || "get").toUpperCase(),
          message: err?.response?.data?.detail || err?.message || "Network Error",
          acao: err?.response?.headers?.["access-control-allow-origin"] || null,
          status: err?.response?.status || 0,
        };
        window.dispatchEvent(new CustomEvent("hac:admin-api-error", { detail }));
      }
    } catch { /* never let the interceptor itself break callers */ }
    return Promise.reject(err);
  },
);

export const getVersusVerdict = (slugA, slugB) =>
  client.get(`/compare/${slugA}-vs-${slugB}/verdict`, { timeout: 60000 }).then((r) => r.data);
export const getSimilarPricedApps = (slug, limit = 3) =>
  client.get(`/apps/${slug}/similar-priced`, { params: { limit } }).then((r) => r.data);
export const listApps = (params = {}) => client.get("/apps", { params }).then((r) => r.data);
export const getApp = (slug) => client.get(`/apps/${slug}`).then((r) => r.data);
export const getNewlyListedApps = (limit = 4) =>
  client.get("/apps/newly-listed", { params: { limit } }).then((r) => r.data);
export const compareApps = (slugs) => client.post("/compare", { slugs }).then((r) => r.data);
export const getRankings = (region) =>
  client.get("/rankings", { params: region ? { region } : {} }).then((r) => r.data);
export const listCategories = () => client.get("/categories").then((r) => r.data);
export const getFeatureTaxonomy = () => client.get("/meta/features").then((r) => r.data);
export const advisorChat = (payload) => client.post("/advisor/chat", payload).then((r) => r.data);
export const submitSponsor = (payload) => client.post("/sponsors", payload).then((r) => r.data);
export const listBlog = (language) => client.get("/blog", { params: language ? { language } : {} }).then((r) => r.data);
export const getBlog = (slug, language) => client.get(`/blog/${slug}`, { params: language ? { language } : {} }).then((r) => r.data);
export const generateBlog = (payload) => client.post("/blog/generate", payload, { timeout: 120000 }).then((r) => r.data);
export const addToQueue = (payload) => client.post("/blog/queue", payload).then((r) => r.data);
export const listQueue = (status) => client.get("/blog/queue/list", { params: status ? { status } : {} }).then((r) => r.data);
export const deleteQueueItem = (id) => client.delete(`/blog/queue/${id}`).then((r) => r.data);
export const processQueueNow = () => client.post("/blog/queue/process", {}, { timeout: 120000 }).then((r) => r.data);

// Admin: Auth
export const adminLogin = (email, password) =>
  client.post("/admin/login", { email, password }).then((r) => r.data);
export const adminVerify2FA = (email, password, code) =>
  client.post("/admin/2fa/verify", { email, password, code }).then((r) => r.data);
export const adminMe = () => client.get("/admin/me").then((r) => r.data);
export const adminReset2FA = () => client.post("/admin/2fa/reset").then((r) => r.data);

// Admin: Ads
export const adminListAds = () => client.get("/admin/ads").then((r) => r.data);
export const adminCreateAd = (payload) => client.post("/admin/ads", payload).then((r) => r.data);
export const adminUpdateAd = (id, payload) => client.patch(`/admin/ads/${id}`, payload).then((r) => r.data);
export const adminDeleteAd = (id) => client.delete(`/admin/ads/${id}`).then((r) => r.data);
export const fetchAds = (placement, ctx) => client.get("/ads", {
  params: {
    placement,
    ...(ctx?.slugs?.length ? { slugs: ctx.slugs.join(",") } : {}),
    ...(ctx?.regions?.length ? { regions: ctx.regions.join(",") } : {}),
  },
}).then((r) => r.data);

// Admin: Boosts
export const adminListBoosts = () => client.get("/admin/boosts").then((r) => r.data);
export const adminCreateBoost = (payload) => client.post("/admin/boosts", payload).then((r) => r.data);
export const adminUpdateBoost = (slug, payload) => client.patch(`/admin/boosts/${slug}`, payload).then((r) => r.data);
export const adminDeleteBoost = (slug) => client.delete(`/admin/boosts/${slug}`).then((r) => r.data);

// Admin: App Media (screenshots + video embeds)
export const adminUploadImage = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return client.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 }).then((r) => r.data);
};
export const getAppMedia = (slug) => client.get(`/apps/${slug}/media`).then((r) => r.data);
export const adminCreateMedia = (slug, payload) => client.post(`/admin/apps/${slug}/media`, payload).then((r) => r.data);
export const adminUpdateMedia = (slug, id, payload) => client.patch(`/admin/apps/${slug}/media/${id}`, payload).then((r) => r.data);
export const adminDeleteMedia = (slug, id) => client.delete(`/admin/apps/${slug}/media/${id}`).then((r) => r.data);
export const adminReorderMedia = (slug, ids) => client.post(`/admin/apps/${slug}/media/reorder`, { ids }).then((r) => r.data);
export const advertStudioGenerate = (payload) => client.post("/advert-studio/generate", payload, { timeout: 120000 }).then((r) => r.data);
export const adminAdvertStudioGenerate = (payload) => client.post("/admin/advert-studio/generate", payload, { timeout: 120000 }).then((r) => r.data);
export const submitAdvert = (payload) => client.post("/advert-submit", payload).then((r) => r.data);
export const adminListAdvertSubmissions = (status) => client.get("/admin/advert-submissions", { params: status ? { status } : {} }).then((r) => r.data);
export const adminApproveAdvert = (id) => client.post(`/admin/advert-submissions/${id}/approve`).then((r) => r.data);
export const adminRejectAdvert = (id, reason) => client.post(`/admin/advert-submissions/${id}/reject`, null, { params: { reason } }).then((r) => r.data);
export const adminDeleteAdvertSubmission = (id) => client.delete(`/admin/advert-submissions/${id}`).then((r) => r.data);
export const adminDataIntegrity = () => client.get("/admin/data-integrity", { timeout: 60000 }).then((r) => r.data);
export const adminDataIntegrityDelete = (collection, recordId) => client.delete(`/admin/data-integrity/${encodeURIComponent(collection)}/${encodeURIComponent(recordId)}`).then((r) => r.data);
export const adminIndexNowPing = (urls) => client.post("/admin/indexnow/ping", { urls }).then((r) => r.data);
export const adminIndexNowInfo = () => client.get("/admin/indexnow/info").then((r) => r.data);
export const adminSeoBoostStatus = () => client.get("/admin/seo-boost/status").then((r) => r.data);
export const adminSeoBoostRunNow = () => client.post("/admin/seo-boost/run-now").then((r) => r.data);
export const adminSeoHealthStatus = () => client.get("/admin/seo-health/status").then((r) => r.data);
export const adminSeoHealthRunNow = () => client.post("/admin/seo-health/run-now").then((r) => r.data);
export const adminSeoHealthHistory = () => client.get("/admin/seo-health/history").then((r) => r.data);
export const adminContentQuality = () => client.get("/admin/content-quality", { timeout: 30000 }).then((r) => r.data);
export const adminDigestStatus = () => client.get("/admin/publisher-digest/status").then((r) => r.data);
export const adminDigestRunNow = () => client.post("/admin/publisher-digest/run-now").then((r) => r.data);
export const adminDigestPreview = (email) => client.post(`/admin/publisher-digest/preview/${encodeURIComponent(email)}`).then((r) => r.data);
export const uploadAdvertImage = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return client.post("/submissions/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};
export const validateDiscountCode = (code) => client.get(`/discount-codes/${encodeURIComponent(code)}`).then((r) => r.data);
export const adminAiGenerateAds = (payload) => client.post("/admin/ads/ai-generate", payload, { timeout: 180000 }).then((r) => r.data);
export const adminDeleteReview = (id) => client.delete(`/admin/reviews/${id}`).then((r) => r.data);
export const adminListReviews = (status) => client.get("/admin/reviews", { params: status ? { status } : {} }).then((r) => r.data);
export const adminSetReviewStatus = (id, status) => client.post(`/admin/reviews/${id}/status`, null, { params: { status } }).then((r) => r.data);
export const mediaUrl = (url) => (url && url.startsWith("/api/") ? `${BACKEND_URL}${url}` : url);

// Admin: Auto-listicles
export const adminListicleTargets = () => client.get("/admin/blog/listicle/preview").then((r) => r.data);
export const adminQueueListicle = (feature_label, language = "en") => client.post("/admin/blog/listicle/queue", { feature_label, language }).then((r) => r.data);
export const adminQueueAllListicles = (languages = ["en"], minutes_between = 5) => client.post("/admin/blog/listicle/queue-all", { languages, minutes_between }).then((r) => r.data);

// Outbound tracked redirect URL builder (no fetch — just the URL for <a href>)
export const goUrl = (slug, source = "direct", extra = {}) => {
  const params = new URLSearchParams({ source });
  if (extra.article) params.set("article", extra.article);
  if (extra.campaign) params.set("campaign", extra.campaign);
  return `${API}/go/${slug}?${params.toString()}`;
};

// Admin: click analytics
export const adminClicksSummary = (days = 30) => client.get("/admin/analytics/clicks", { params: { days } }).then((r) => r.data);
export const adminSubmissionsAnalytics = (days = 90) => client.get("/admin/analytics/submissions", { params: { days } }).then((r) => r.data);
export const adminBannerAnalytics = (days = 30) => client.get("/admin/analytics/banners", { params: { days } }).then((r) => r.data);
export const trackBanner = (banner, event) => client.post("/banner/track", { banner, event }).then((r) => r.data).catch(() => null);

// Public contact form
export const submitContact = (payload) => client.post("/contact", payload).then((r) => r.data);

// Admin contact inbox
export const adminListContact = (status) => client.get("/admin/contact", { params: status ? { status } : {} }).then((r) => r.data);
export const adminUpdateContact = (id, status) => client.patch(`/admin/contact/${id}`, { status }).then((r) => r.data);
export const adminDeleteContact = (id) => client.delete(`/admin/contact/${id}`).then((r) => r.data);
export const adminReplyContact = (id, body, subject = "") => client.post(`/admin/contact/${id}/reply`, { body, subject }, { timeout: 30000 }).then((r) => r.data);
export const adminReplySponsor = (id, body, subject = "") => client.post(`/admin/sponsors/${id}/reply`, { body, subject }, { timeout: 30000 }).then((r) => r.data);

export const adminListSponsors = (status) => client.get("/admin/sponsors", { params: status ? { status } : {} }).then((r) => r.data);
export const adminUpdateSponsor = (id, status) => client.patch(`/admin/sponsors/${id}`, { status }).then((r) => r.data);
export const adminDeleteSponsor = (id) => client.delete(`/admin/sponsors/${id}`).then((r) => r.data);

// ===== Auction / Bidder =====
const BIDDER_TOKEN_KEY = "hac_bidder_token";
export const getBidderToken = () => localStorage.getItem(BIDDER_TOKEN_KEY);
export const setBidderToken = (t) => { if (t) localStorage.setItem(BIDDER_TOKEN_KEY, t); else localStorage.removeItem(BIDDER_TOKEN_KEY); };

export const bidderRegister = (payload) => client.post("/bidder/register", payload).then((r) => { setBidderToken(r.data.token); return r.data; });
export const bidderLogin = (payload) => client.post("/bidder/login", payload).then((r) => { setBidderToken(r.data.token); return r.data; });
export const bidderMe = () => client.get("/bidder/me").then((r) => r.data);
export const bidderLogout = () => setBidderToken(null);
export const bidderDashboard = () => client.get("/bidder/dashboard").then((r) => r.data);

export const fetchCurrentAuction = () => client.get("/auction/current").then((r) => r.data);
export const fetchPastWinners = (limit = 8) => client.get("/auction/past-winners", { params: { limit } }).then((r) => r.data);
export const fetchAuctionBids = (auctionId) => client.get(`/auction/${auctionId}/bids`).then((r) => r.data);
export const placeBid = (auctionId, amount_eur) => client.post(`/auction/${auctionId}/bid`, { amount_eur }).then((r) => r.data);

// Admin auction
export const adminListAuctions = () => client.get("/admin/auctions").then((r) => r.data);
export const adminCreateAuction = (payload) => client.post("/admin/auctions", payload).then((r) => r.data);
export const adminPatchAuction = (id, payload) => client.patch(`/admin/auctions/${id}`, payload).then((r) => r.data);
export const adminForceCloseAuction = (id) => client.post(`/admin/auctions/${id}/close`).then((r) => r.data);
export const adminDeleteAuction = (id) => client.delete(`/admin/auctions/${id}`).then((r) => r.data);
export const adminListAuctionBids = (id) => client.get(`/admin/auctions/${id}/bids`).then((r) => r.data);
export const adminPushAuctionBanner = (id, payload) => client.post(`/admin/auctions/${id}/push-banner`, payload).then((r) => r.data);

// Admin: Apps CRUD
export const adminCreateApp = (payload) => client.post("/admin/apps", payload).then((r) => r.data);
export const adminUpdateApp = (slug, payload) => client.patch(`/admin/apps/${slug}`, payload).then((r) => r.data);
export const adminInboxSummary = () => client.get("/admin/inbox-summary").then((r) => r.data);
export const adminVerifyOwner = (slug, email) =>
  client.post(`/admin/apps/${slug}/verify-owner`, { email: email || null }).then((r) => r.data);
export const adminDeleteApp = (slug) => client.delete(`/admin/apps/${slug}`).then((r) => r.data);
export const adminListAllApps = () => client.get("/admin/apps").then((r) => r.data);

// ---- AI app discovery ----
export const adminDiscoverApps = (payload) =>
  client.post("/admin/apps/discover", payload, { timeout: 30000 }).then((r) => r.data);
export const adminDiscoveryStatus = () =>
  client.get("/admin/apps/discovery-status").then((r) => r.data);

// ---- App outreach ----
export const adminFindAppEmail = (slug) =>
  client.post(`/admin/apps/${slug}/find-email`, {}, { timeout: 45000 }).then((r) => r.data);
export const adminSendAppOutreach = (slug, email, cc = "") =>
  client.post(`/admin/apps/${slug}/send-outreach`, { email, cc }, { timeout: 30000 }).then((r) => r.data);
export const adminOutreachStatus = () =>
  client.get("/admin/outreach-status").then((r) => r.data);
export const adminAppOutreachLog = (slug) =>
  client.get(`/admin/apps/${slug}/outreach-log`).then((r) => r.data);
export const adminOutreachBatch = (dryRun = false) =>
  client.post("/admin/apps/outreach-batch", { dry_run: dryRun }, { timeout: 30000 }).then((r) => r.data);
export const adminOutreachBatchStatus = () =>
  client.get("/admin/apps/outreach-batch-status").then((r) => r.data);
export const adminOutreachFollowups = () =>
  client.get("/admin/apps/outreach-followups").then((r) => r.data);
export const adminSendFollowup = (slug, force = false) =>
  client.post(`/admin/apps/${slug}/send-followup`, { force }, { timeout: 30000 }).then((r) => r.data);
export const adminOutreachFollowupBatch = (dryRun = false) =>
  client.post("/admin/apps/outreach-followup-batch", { dry_run: dryRun }, { timeout: 30000 }).then((r) => r.data);
export const adminOutreachReset = (slug) =>
  client.post(`/admin/apps/${slug}/outreach-reset`).then((r) => r.data);
export const adminOutreachOptOut = (slug, email = "") =>
  client.post(`/admin/apps/${slug}/outreach-optout`, { email }).then((r) => r.data);
export const adminOutreachOptOutClear = (slug) =>
  client.delete(`/admin/apps/${slug}/outreach-optout`).then((r) => r.data);

// ---- Claim edits review ----
export const adminListClaimEdits = (status = "") =>
  client.get("/admin/claim-edits", { params: status ? { status } : {} }).then((r) => r.data);
export const adminApproveClaimEdit = (id) =>
  client.post(`/admin/claim-edits/${id}/approve`).then((r) => r.data);
export const adminRejectClaimEdit = (id, reason = "") =>
  client.post(`/admin/claim-edits/${id}/reject`, { reason }).then((r) => r.data);

// Send "your app is now live" email to the registered owner of an approved app.
export const adminNotifyAppPublished = (slug) =>
  client.post(`/admin/apps/${slug}/notify-published`).then((r) => r.data);
// Invite the verified owner of a CLAIMED app to finish their profile (magic-link).
export const adminInviteCompleteProfile = (slug) =>
  client.post(`/admin/apps/${slug}/invite-complete-profile`).then((r) => r.data);
export const adminEmailSmtpTest = () =>
  client.get("/admin/email/smtp-test").then((r) => r.data);
export const adminEmailSendTest = () =>
  client.post("/admin/email/send-test").then((r) => r.data);
export const adminToggleActive = (slug, active, reason = null) => client.post(`/admin/apps/${slug}/active`, { active, reason }).then((r) => r.data);

// Public app submissions
export const submitApp = (payload) => client.post("/submissions", payload).then((r) => r.data);
export const submitAppUpload = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return client.post("/submissions/upload", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 }).then((r) => r.data);
};

// Admin: submissions review
export const adminListSubmissions = (status) => client.get("/admin/submissions", { params: status ? { status } : {} }).then((r) => r.data);
export const adminGetSubmission = (id) => client.get(`/admin/submissions/${id}`).then((r) => r.data);
export const adminUpdateSubmission = (id, payload) => client.patch(`/admin/submissions/${id}`, payload).then((r) => r.data);
export const adminApproveSubmission = (id, force = false, overwrite = false) => {
  const params = {};
  if (force) params.force = true;
  if (overwrite) params.overwrite = true;
  return client.post(`/admin/submissions/${id}/approve`, {}, { params }).then((r) => r.data);
};
export const adminRejectSubmission = (id) => client.post(`/admin/submissions/${id}/reject`).then((r) => r.data);
export const adminDeleteSubmission = (id) => client.delete(`/admin/submissions/${id}`).then((r) => r.data);
export const adminUpdateInvoice = (id, invoice_status) => client.patch(`/admin/submissions/${id}/invoice`, { invoice_status }).then((r) => r.data);

// Admin: premium toggle on existing apps
export const adminTogglePremium = (slug, premium, premium_tier = "monthly") => client.post(`/admin/apps/${slug}/premium`, { premium, premium_tier }).then((r) => r.data);

// Admin: i18n prewarm
export const adminPrewarmI18n = (source, source_hash, languages) => client.post("/admin/i18n/prewarm", { source, source_hash, languages }).then((r) => r.data);
export const adminI18nStatus = (source_hash) => client.get("/admin/i18n/status", { params: { source_hash } }).then((r) => r.data);

// Admin: newsletter
export const adminGetEmailSettings = () => client.get("/admin/email-settings").then((r) => r.data);
export const adminSetEmailSettings = (payload) => client.post("/admin/email-settings", payload).then((r) => r.data);
export const adminEmailTest = (to) => client.post("/admin/email-test", { to }).then((r) => r.data);
export const adminListSubscribers = () => client.get("/admin/newsletter-subscribers").then((r) => r.data);
export const adminNewsletterSendNow = () => client.post("/admin/newsletter-send-now").then((r) => r.data);
export const adminNewsletterHistory = () => client.get("/admin/newsletter-history").then((r) => r.data);

// Admin: publisher claims
export const adminListClaims = () => client.get("/admin/claims").then((r) => r.data);
export const adminDecideClaim = (claim_id, decision, note = "") => client.post(`/admin/claims/${claim_id}/decide`, { decision, note }).then((r) => r.data);

// Admin: discount codes
export const adminListDiscounts = () => client.get("/admin/discount-codes").then((r) => r.data);
export const adminCreateDiscount = (payload) => client.post("/admin/discount-codes", payload).then((r) => r.data);
export const adminDeleteDiscount = (code) => client.delete(`/admin/discount-codes/${encodeURIComponent(code)}`).then((r) => r.data);

// Billing portal (advertiser self-service)
export const billingRequestCode = (email) => client.post("/billing/request-code", { email }).then((r) => r.data);
export const billingVerifyCode = (email, code) => client.post("/billing/verify-code", { email, code }).then((r) => r.data);
export const billingListInvoices = (token) => client.get("/billing/invoices", { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.data);
export const adminListInvoices = () => client.get("/admin/invoices").then((r) => r.data);

// Admin: backups
export const adminListBackups = () => client.get("/admin/backup/list").then((r) => r.data);
export const adminCreateBackup = () =>
  client.post("/admin/backup/create", {}, { timeout: 120000 }).then((r) => r.data);
export const adminDeleteBackup = (id) => client.delete(`/admin/backup/${id}`).then((r) => r.data);
export const adminRunWeeklyBackup = () =>
  client.post("/admin/backup/run-weekly-now", {}, { timeout: 120000 }).then((r) => r.data);

// Admin: restore
export const adminRestoreDryRun = ({ backupId, file }) => {
  const form = new FormData();
  if (backupId) form.append("backup_id", backupId);
  if (file) form.append("file", file);
  return client
    .post("/admin/restore/dry-run", form, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 180000,
    })
    .then((r) => r.data);
};
export const adminRestoreExecute = ({ backupId, collections, confirmation, totpCode }) =>
  client
    .post(
      "/admin/restore/execute",
      { backup_id: backupId, collections, confirmation, totp_code: totpCode },
      { timeout: 180000 }
    )
    .then((r) => r.data);
export const adminRestoreExecuteUpload = ({ file, collections, confirmation, totpCode }) => {
  const form = new FormData();
  form.append("file", file);
  form.append("collections_json", JSON.stringify(collections));
  form.append("confirmation", confirmation);
  form.append("totp_code", totpCode);
  return client
    .post("/admin/restore/execute-upload", form, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 240000,
    })
    .then((r) => r.data);
};
export const adminRestoreHistory = () =>
  client.get("/admin/restore/history").then((r) => r.data);

// Admin: visibility — bulk + single
export const adminAppsVisibility = () =>
  client.get("/admin/apps-visibility").then((r) => r.data);
export const adminAppVisibility = (slug) =>
  client.get(`/admin/apps/${slug}/visibility`).then((r) => r.data);

